import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlanCards } from "@/components/PlanCards";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";

export default function Signup() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [plan, setPlan] = useState("standard");
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/subscription/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await signup({ ...form, email: form.email.trim(), plan });
    setLoading(false);
    if (res.ok) navigate("/dashboard", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-2/5 bg-[#111111] flex-col justify-between p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Neo" className="h-[70px] w-auto object-contain bg-white rounded-lg p-2" />
          <div>
            <div className="font-display font-semibold text-white text-2xl tracking-wide">NEO REAL ESTATE</div>
            <div className="text-[20px] text-gray-500 tracking-wide">MANAGEMENT SAAS</div>
          </div>
        </div>
        <div>
          <h1 className="font-display text-4xl font-light text-white leading-tight mb-4">
            14日間、<span className="text-brand">無料でお試し。</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-md leading-relaxed">
            クレジットカード不要ですぐに始められます。物件・入居者・契約・家賃・収支まで、すべてをひとつのシステムで。
          </p>
        </div>
        <div className="text-xs text-gray-600">© 2026 Neo Real Estate Management</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 border border-white/5 rounded-full" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-3xl py-8">
          {step === 1 && (
            <div data-testid="signup-step-plan">
              <h2 className="text-2xl font-medium mb-1">プランを選択</h2>
              <p className="text-sm text-muted-foreground mb-6">14日間の無料トライアル。いつでも変更・解約できます。</p>
              <div className="inline-flex items-center bg-gray-100 rounded-sm p-1 mb-6" data-testid="cycle-toggle">
                <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
                  className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${cycle === "monthly" ? "bg-white shadow-sm font-medium" : "text-muted-foreground"}`}>月払い</button>
                <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly"
                  className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${cycle === "yearly" ? "bg-white shadow-sm font-medium" : "text-muted-foreground"}`}>年払い（2ヶ月分お得）</button>
              </div>
              <PlanCards plans={plans} cycle={cycle} currentPlan={plan} onSelect={setPlan} />
              <div className="mt-6 flex items-center justify-between">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="signup-to-login">
                  すでにアカウントをお持ちの方
                </Link>
                <Button onClick={() => setStep(2)} className="bg-brand hover:bg-brand-hover text-white h-11 px-6" data-testid="signup-next">
                  次へ（{plans.find((p) => p.key === plan)?.name}プラン）<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="max-w-sm mx-auto" data-testid="signup-step-account">
              <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground" data-testid="signup-back">
                <ArrowLeft className="w-4 h-4" />プラン選択へ戻る
              </button>
              <h2 className="text-2xl font-medium mb-1">アカウント作成</h2>
              <p className="text-sm text-muted-foreground mb-6">{plans.find((p) => p.key === plan)?.name}プラン・14日間無料</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">会社名</Label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="signup-company" className="h-11" placeholder="株式会社〇〇不動産" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">お名前</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="signup-name" className="h-11" placeholder="山田 太郎" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">メールアドレス</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="signup-email" className="h-11" placeholder="you@example.com" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">パスワード（8文字以上）</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="signup-password" className="h-11" placeholder="••••••••" />
                </div>
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="signup-error">{error}</div>}
                <Button type="submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-hover text-white" data-testid="signup-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>無料トライアルを開始<ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">クレジットカードは不要です。トライアル中も全機能をお使いいただけます。</p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
